import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { fhirClient, useDataset, useFHIRQuery } from '../../../../util/api';
import { formatCodingToken, parseReference } from '@lifeomic/fhir-constants';
import { unwrapBundle } from '@lifeomic/phc-web-toolkit/src/util/fhir';
import { UseSurveysListProvider } from '@lifeomic/phc-web-toolkit/src/components/ManageSurveys/SurveysList/useSurveysList';
import {
  fetchSnapshotDataSourcesForPatients,
  mapSnapshotDataToPatient,
} from '../../OnboardingSnapshot/dataSources';
import { formatSnapshots } from '../../OnboardingSnapshot/components/OnboardingSnapshotTable';
import { IndividualOnboardingView } from '../../OnboardingSnapshot/components/IndividualOnboardingView';
import { formatMember } from '../../../workflows/utils';
import { MEMBER_APP_PERSON_USERNAME_QUERY_KEY } from '../../../workflows/memberAppUsername';
import { fhirSystems } from '../../OnboardingSnapshot/fhir-systems';
import type { MemberOnboardingData } from '../../OnboardingSnapshot';
import { WithPlatformDefaults } from '@lifeomic/react-client';

const messages = defineMessages({
  snapshotUnavailable: {
    id: 'MemberDetail.snapshotUnavailable',
    defaultMessage: 'Unable to load onboarding snapshot for this member.',
  },
  snapshotEmpty: {
    id: 'MemberDetail.snapshotEmpty',
    defaultMessage: 'No onboarding snapshot data available for this member.',
  },
});

const useStyles = makeStyles(theme => ({
  pageContent: {
    flex: 1,
    minHeight: 0,
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
    padding: theme.spacing(2),
  },
}));

type OnboardingSnapshotContentProps = {
  member: WithPlatformDefaults<fhir3.Patient> | null;
  memberId: string;
  fallback: React.ReactNode;
  onMemberPatientRefetched?: () => Promise<void>;
};

export const OnboardingSnapshotContent: React.FC<
  OnboardingSnapshotContentProps
> = ({ member, memberId, fallback, onMemberPatientRefetched }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const dataset = useDataset();
  const queryClient = useQueryClient();

  const handleMemberContactSaved = React.useCallback(async () => {
    await onMemberPatientRefetched?.();
    // Yield so the refetched Patient is committed into this subtree before onboarding
    // queries refetch (avoids rebuilding snapshots from stale `member` props).
    await new Promise<void>(resolve => {
      queueMicrotask(resolve);
    });
    await queryClient.invalidateQueries({
      queryKey: ['member-onboarding-data'],
    });
    await queryClient.invalidateQueries({
      queryKey: MEMBER_APP_PERSON_USERNAME_QUERY_KEY,
    });
  }, [onMemberPatientRefetched, queryClient]);

  const physiciansQuery = useFHIRQuery('GET /v1/fhir/dstu3/Practitioner', {
    _tag: [
      dataset.codingToken,
      formatCodingToken({
        system: fhirSystems.practitioner.role.system,
        code: fhirSystems.practitioner.role.values.physician,
      }),
    ],
    pageSize: 1000,
  });

  const managingOrganizationId = parseReference(
    member?.managingOrganization,
  )?.id;

  const centerQuery = useQuery({
    queryKey: ['member-onboarding-center', dataset?.id, managingOrganizationId],
    queryFn: async (): Promise<string | undefined> => {
      if (!managingOrganizationId) return undefined;

      const response = await fhirClient.request(
        'GET /v1/fhir/dstu3/Organization',
        {
          _tag: dataset.codingToken,
          _id: managingOrganizationId,
          pageSize: 1,
        },
      );
      const organizations = unwrapBundle(response.data) as Array<
        WithPlatformDefaults<fhir3.Organization>
      >;
      return organizations[0]?.name;
    },
    enabled: Boolean(member) && Boolean(managingOrganizationId),
  });

  const onboardingQuery = useQuery({
    queryKey: [
      'member-onboarding-data',
      memberId,
      dataset?.id,
      member?.meta?.lastUpdated ?? member?.meta?.versionId,
      physiciansQuery.data,
      centerQuery.data,
    ],
    queryFn: async (): Promise<MemberOnboardingData | null> => {
      if (!memberId || !member || !dataset) return null;
      const referenceDate = new Date();
      const allData = await fetchSnapshotDataSourcesForPatients({
        currentPatientIds: memberId,
        dataset,
        referenceDate,
      });
      const patientData = mapSnapshotDataToPatient(allData, member?.id ?? '');
      const providers = physiciansQuery.data
        ? unwrapBundle(physiciansQuery.data)
        : [];
      const combined = {
        patient: member,
        consents: patientData.consents,
        surveyResponses: patientData.surveyResponses,
        appointments: patientData.appointments,
        keyAppointments: patientData.keyAppointments,
        member: formatMember(member, {
          formatMessage,
          providers,
          centerName: centerQuery.data,
        }),
      };
      return {
        ...combined,
        snapshots: formatSnapshots(combined),
      };
    },
    enabled: Boolean(memberId) && Boolean(member) && Boolean(dataset),
  });

  if (!member) {
    return <>{fallback}</>;
  }

  if (onboardingQuery.isLoading) {
    return (
      <Box fullWidth fullHeight align="center" justify="center" gap={1}>
        <DotLoader />
      </Box>
    );
  }

  if (onboardingQuery.isError) {
    return (
      <Box fullWidth fullHeight align="center" justify="center">
        <Text size="subbody">
          {formatMessage(messages.snapshotUnavailable)}
        </Text>
      </Box>
    );
  }

  if (!onboardingQuery.data) {
    return (
      <Box fullWidth fullHeight align="center" justify="center">
        <Text size="subbody">{formatMessage(messages.snapshotEmpty)}</Text>
      </Box>
    );
  }

  return (
    <Box className={classes.pageContent}>
      <UseSurveysListProvider>
        <IndividualOnboardingView
          {...onboardingQuery.data}
          onMemberContactSaved={handleMemberContactSaved}
        />
      </UseSurveysListProvider>
    </Box>
  );
};
